/**
 * Note:
 * This file should be synced with the corresponding layout in Contentful CMS.
 */
import {
  arrayOf,
  bool,
  customType,
  extend,
  fragment,
  namedFragment,
  number,
  params,
  shape,
  string,
  typename
} from '@thd-nucleus/data-sources';
import { Spotlight, SpotlightContainer } from '@thd-olt-component-react/spotlight';
import { ShopByCategory } from '@thd-olt-component-react/shop-by-category';
import {
  PromoVisualNavigation,
  VisualNavigation
} from '@thd-olt-component-react/visual-navigation';
import { ComponentSelector, Slot } from '@thd-olt-component-react/component-selector';
import { MedioInline,
  MedioComposableBanner,
  PiccolaPencil,
  PiccolaComposableBanner } from '@thd-olt-component-react/internal-marketing-banner';
import { EventCountdownTimer } from '@thd-olt-component-react/event-countdown-timer';
import { NewspaperModule } from '@thd-olt-component-react/newspaper-module';
import { ProductShelf } from '@thd-olt-component-react/product-shelf';
import { Resource } from '@thd-olt-component-react/resource';
import { CalloutCard } from '@thd-olt-component-react/callout_card';
import { ContentGalleryCard } from '@thd-olt-component-react/content-gallery-card';
import { HeroFlattenImage } from '@thd-olt-component-react/hero';

const commonComponentFields = {
  id: string(),
  customRenderId: string(),
  __typename: string(),
};

let CapabilitiesTile = {};
CapabilitiesTile.dataModel = {
  component: params({
    id: string().isRequired(),
  }).shape({
    CapabilitiesTile: fragment().shape({
      link: string(),
      id: string(),
      __typename: string(),
      title: string(),
      description: string(),
      cta: string(),
      previewImage: shape({
        damContentSelector: shape({
          assetData: arrayOf(shape({
            selectedImageUrl: string()
          }))
        }),
        damDownloadedContent: shape({
          url: string()
        })
      }),
    }),
  }),
};

const HomeDecorCard = namedFragment({
  inline: true,
  fragmentType: 'HomeDecorCard',
  fragmentAlias: 'HomeDecorCardFields'
}).shape({
  id: string(),
  __typename: typename('HomeDecorCard'),
  link: string(),
  componentName: string(),
  cta: string(),
  showCTA: bool(),
  categoryComponents: arrayOf(shape({
    id: string(),
    title: string(),
    visualNavigationList: arrayOf(shape({
      id: string(),
      title: string(),
      link: string(),
      previewImage: shape({
        damDownloadedContent: shape({
          url: string()
        })
      })
    }))
  })),
});

const AnchorLinks = namedFragment({
  inline: true,
  fragmentType: 'AnchorLinks',
  fragmentAlias: 'AnchorLinksFields'
}).shape({
  id: string(),
  __typename: typename('AnchorLinks'),
  theme: string(),
  linkList: arrayOf(shape({
    id: string(),
    label: string(),
    href: string(),
  })),
});

const seoShape = shape({
  title: string(),
  description: string(),
  facebookAdmins: string(),
  siteName: string(),
  type: string(),
  canonicalURL: string(),
  keywords: arrayOf(string()),
  no_index: bool(),
  no_follow: bool(),
});

const SlotFragment = namedFragment({
  inline: true,
  fragmentType: 'Slot',
  fragmentAlias: 'SlotFields'
}).shape({
  id: string(),
  name: string(),
  slotNumber: number(),
  content: shape({
    ...Spotlight.dataModel.component,
    ...HeroFlattenImage.dataModel.component,
    HomeDecorCard,
    ...VisualNavigation.dataModel.component,
    ...Resource.dataModel.component,
    ...CalloutCard.dataModel.component,
    ...PromoVisualNavigation.dataModel.component,
    ...ProductShelf.dataModel.component,
    ...MedioInline.dataModel.component,
    ...MedioComposableBanner.dataModel.component,
    ...PiccolaPencil.dataModel.component,
    ...PiccolaComposableBanner.dataModel.component,
    ...ContentGalleryCard.dataModel.component
  })
});

Slot.dataModel = {
  component: params({
    id: string().isRequired(),
    componentClass: customType('ComponentClass')
      .enum(['Slot'])
      .isRequired(),
  }).shape({
    Slot: SlotFragment,
  })
};

const ComponentSelectorFragment = namedFragment({
  inline: true,
  fragmentType: 'ComponentSelector',
  fragmentAlias: 'ComponentSelectorFields'
}).shape({
  id: string(),
  targetingType: shape({
    AdobeTarget: fragment().shape({
      id: string(),
      mbox: string(),
    }),
    MultiArmBandit: fragment().shape({
      id: string(),
      campaignId: string(),
      channel: string(),
    }),
    Innervate: fragment().shape({
      id: string(),
      tagID: string(),
      adkey: string()
    })
  }),
  defaultComponent: arrayOf(
    shape({
      ...Spotlight.dataModel.component,
      ...Slot.dataModel.component,
      ...HeroFlattenImage.dataModel.component,
      HomeDecorCard,
      ...VisualNavigation.dataModel.component,
      ...Resource.dataModel.component,
      ...CalloutCard.dataModel.component,
      ...PromoVisualNavigation.dataModel.component,
      ...ProductShelf.dataModel.component,
      ...MedioInline.dataModel.component,
      ...MedioComposableBanner.dataModel.component,
      ...PiccolaPencil.dataModel.component,
      ...PiccolaComposableBanner.dataModel.component,
      ...ContentGalleryCard.dataModel.component
    }),
  ),
  variations: arrayOf(
    shape({
      ...Spotlight.dataModel.component,
      ...Slot.dataModel.component,
      ...HeroFlattenImage.dataModel.component,
      HomeDecorCard,
      ...VisualNavigation.dataModel.component,
      ...Resource.dataModel.component,
      ...CalloutCard.dataModel.component,
      ...PromoVisualNavigation.dataModel.component,
      ...ProductShelf.dataModel.component,
      ...MedioInline.dataModel.component,
      ...MedioComposableBanner.dataModel.component,
      ...PiccolaPencil.dataModel.component,
      ...PiccolaComposableBanner.dataModel.component,
      ...ContentGalleryCard.dataModel.component
    }),
  ),
});

ComponentSelector.dataModel = {
  component: params({
    id: string().isRequired(),
    componentClass: customType('ComponentClass')
      .enum(['ComponentSelector'])
      .isRequired(),
  }).shape({
    ComponentSelector: ComponentSelectorFragment,
    ...Spotlight.dataModel.component,
    ...Slot.dataModel.component,
    ...HeroFlattenImage.dataModel.component,
    HomeDecorCard,
    ...VisualNavigation.dataModel.component,
    ...Resource.dataModel.component,
    ...CalloutCard.dataModel.component,
    ...PromoVisualNavigation.dataModel.component,
    ...ProductShelf.dataModel.component,
    ...MedioInline.dataModel.component,
    ...MedioComposableBanner.dataModel.component,
    ...PiccolaPencil.dataModel.component,
    ...PiccolaComposableBanner.dataModel.component,
    ...ContentGalleryCard.dataModel.component
  }),
};

const heroCarouselShape = shape({
  id: string(),
  customRenderId: string(),
  isCarousel: bool(),
  isAutoplay: bool(),
  theme: string(),
  components: arrayOf(
    shape({
      /* Keep HFI at top to preserve current caching strategy. DAMMedia caching may break if order
      is changed without consideration. */
      ...HeroFlattenImage.dataModel.component,
      ...Spotlight.dataModel.component,
      ...SpotlightContainer.dataModel.specializedContainer,
      ...ComponentSelector.dataModel.component,
    }),
  ),
});

let PopularCategories = {};
PopularCategories.dataModel = {
  ...commonComponentFields,
};

let Recommendations = {};
Recommendations.dataModel = {
  ...commonComponentFields,
  theme: string(),
};

let SponsoredBanner = {};
SponsoredBanner.dataModel = {
  ...commonComponentFields,
  schema: string(),
  imgDesktop: string(),
  imgMobile: string(),
  clickthruUrl: string(),
  theme: string(),
  showBanner: bool(),
  specialDefaultBanner: shape({
    imgDesktop: string(),
    imgMobile: string(),
    clickthruUrl: string(),
    useSpecialBanner: bool(),
  }),
};

let PersonalizedHomeServices = {};
PersonalizedHomeServices.dataModel = {
  ...commonComponentFields,
};

let PromotionCard = {};
PromotionCard.dataModel = {
  ...commonComponentFields,
};

let RecentOrders = {};
RecentOrders.dataModel = {
  ...commonComponentFields,
};

let RecentPurchases = {};
RecentPurchases.dataModel = {
  ...commonComponentFields,
};

let SignIn = {};
SignIn.dataModel = {
  ...commonComponentFields,
};

let SpecialBuyOfTheDay = {};
SpecialBuyOfTheDay.dataModel = {
  ...commonComponentFields,
  theme: string(),
};

let SpecialBuyOfTheWeek = {};
SpecialBuyOfTheWeek.dataModel = {
  ...commonComponentFields,
};

let LoyaltyBenefits = {};
LoyaltyBenefits.dataModel = {
  ...commonComponentFields,
};

let Projects = {};
Projects.dataModel = {
  ...commonComponentFields,
  theme: string()
};

let Sweepstakes = {};
Sweepstakes.dataModel = {
  ...commonComponentFields,
};

let PrivateLabelCreditCard = {};
PrivateLabelCreditCard.dataModel = {
  ...commonComponentFields,
};

let OnlineWorkshops = {};
OnlineWorkshops.dataModel = {
  ...commonComponentFields,
};

let UserList = {};
UserList.dataModel = {
  ...commonComponentFields,
};

let Quotes = {};
Quotes.dataModel = {
  id: string().isRequired(),
  customRenderId: string(),
  __typename: string(),
  theme: string()
};

const SectionDM = namedFragment({
  inline: true,
  fragmentType: 'Section',
  fragmentAlias: 'SectionDM'
}).shape({
  id: string(),
  title: string(),
  customRenderId: string(),
  isCarousel: bool(),
  theme: string(),
  sectionCTAButton: shape({
    callToAction: string(),
    destinationLink: string()
  }),
  components: arrayOf(
    shape({
      /* Keep HFI at top to preserve current caching strategy. DAMMedia caching may break if order
      is changed without consideration. */
      ...HeroFlattenImage.dataModel.component,
      ...Spotlight.dataModel.component,
      ...SpotlightContainer.dataModel.specializedContainer,
      ...ComponentSelector.dataModel.component,
      ...CapabilitiesTile.dataModel.component,
      ...VisualNavigation.dataModel.component,
      ...PromoVisualNavigation.dataModel.component,
      ...MedioInline.dataModel.component,
      ...PiccolaPencil.dataModel.component,
      ...NewspaperModule.dataModel.component,
      ...ProductShelf.dataModel.component,
      ...Resource.dataModel.component,
      ...CalloutCard.dataModel.component,
      ...ContentGalleryCard.dataModel.component,
      Sweepstakes: fragment().shape(Sweepstakes.dataModel),
    }),
  ),
  __typename: typename('Section'),
});

const internalMarketingBannerShape = shape({
  ...MedioInline.dataModel.component,
  ...MedioComposableBanner.dataModel.component,
  ...PiccolaPencil.dataModel.component,
  ...PiccolaComposableBanner.dataModel.component,
});

const targetingTypeShape = shape({
  AdobeTarget: namedFragment({
    inline: true,
    fragmentType: 'AdobeTarget'
  }).shape({
    id: string(),
    mbox: string(),
    __typename: typename('AdobeTarget')
  }),
  Innervate: namedFragment({
    inline: true,
    fragmentType: 'Innervate'
  }).shape({
    id: string(),
    tagID: string(),
    adkey: string(),
    __typename: typename('Innervate')
  }),
  // MultiArmBandit isn't being used but it is included here because taking it out may cause issues until it is fully
  // removed from ComponentSelector logic.
  MultiArmBandit: namedFragment({
    inline: true,
    fragmentType: 'MultiArmBandit'
  }).shape({
    id: string(),
    campaignId: string(),
    __typename: typename('MultiArmBandit')
  }),
});

const HeroFlattenImageSlot = {};
HeroFlattenImageSlot.dataModel = {
  component: params({
    id: string().isRequired(),
    componentClass: customType('ComponentClass').enum(['Slot']).isRequired()
  }).shape({
    Slot: namedFragment({
      inline: true,
      fragmentType: 'Slot',
      fragmentAlias: 'HeroFlattenImageSlot'
    }).shape({
      componentName: string(),
      id: string(),
      slotNumber: string(),
      content: shape({
        ...HeroFlattenImage.dataModel.component
      })
    })
  })
};

const BentoHeroSelector = {};
BentoHeroSelector.dataModel = {
  component: params({
    id: string().isRequired(),
    componentClass: customType('ComponentClass').enum(['ComponentSelector']).isRequired()
  }).shape({
    ComponentSelector: namedFragment({
      inline: true,
      fragmentType: 'ComponentSelector',
      fragmentAlias: 'BentoHeroSelector'
    }).shape({
      componentName: string(),
      id: string(),
      targetingType: targetingTypeShape,
      defaultComponent: shape({
        ...HeroFlattenImageSlot.dataModel.component
      }),
      variations: arrayOf(
        shape({
          ...HeroFlattenImageSlot.dataModel.component
        })
      ),
      __typename: typename('ComponentSelector')
    })
  })
};

const Section = namedFragment({
  inline: false,
  fragmentType: 'Section',
  fragmentAlias: 'BentoSection'
}).shape({
  id: string(),
  isCarousel: bool(),
  isAutoplay: bool(),
  components: arrayOf(shape({
    ...BentoHeroSelector.dataModel.component,
    ...HeroFlattenImage.dataModel.component
  })),
  customRenderId: string(),
  theme: string(),
  __typename: string()
});

export const BentoHeroModel = {};
BentoHeroModel.dataModel = {
  id: string(),
  componentName: string(),
  customRenderId: string(),
  firstBay: Section,
  secondBay: Section,
  thirdBay: Section,
  fourthBay: Section,
  bentoSponsoredBanner: shape({
    SponsoredBanner: fragment().shape({
      ...SponsoredBanner.dataModel
    })
  }),
  theme: string()
};

export const BentoHero = {};
BentoHero.dataModel = extend({
  component: params({
    id: string()
      .isRequired(),
    componentClass: customType('ComponentClass')
      .enum(['BentoHero'])
      .isRequired(),
  })
    .shape({
      BentoHero: namedFragment({
        inline: true,
        fragmentType: 'BentoHero',
        fragmentAlias: 'BentoHeroHomeV1'
      }).shape({
        ...BentoHeroModel.dataModel
      })
    }),
});

export const authB2cDataModelServerSideCall = extend({
  layouts: params({
    slug: string().isRequired(),
    customerType: string(),
  }).shape({
    id: string(),
    content: shape({
      __typename: string(),
      PersonalizedHomePage: fragment().shape({
        bentoHero: shape(BentoHero.dataModel.component),
        id: string(),
        pageTheme: string(),
        pageThemeSeparator: string(),
        quickLinks: shape({ AnchorLinks }),
        sweepstakesBanner: shape(Sweepstakes.dataModel),
        internalMarketingBanner: internalMarketingBannerShape,
        // you need this here too because the query is shaped at a lower level
        // it doesnt magically know how to merge the two heroCarousels because the nesting is odd
        heroCarousel: heroCarouselShape,
        welcomeMessage: string(),
      }),
    }),
    seo: seoShape,
  }),
});

export const authB2cDataModelClientSideCall = extend({
  layouts: params({
    slug: string().isRequired(),
    customerType: string(),
  }).shape({
    id: string(),
    content: shape({
      __typename: string(),
      PersonalizedHomePage: fragment().shape({
        id: string(),
        pageTheme: string(),
        pageThemeSeparator: string(),
        componentOrder: shape({
          ...Spotlight.dataModel.component,
          ...EventCountdownTimer.dataModel.component,
          Recommendations: fragment().shape(Recommendations.dataModel),
          Section: SectionDM,
          SponsoredBanner: fragment().shape(SponsoredBanner.dataModel),
          SpecialBuyOfTheDay: fragment().shape(SpecialBuyOfTheDay.dataModel),
          SpecialBuyOfTheWeek: fragment().shape(SpecialBuyOfTheWeek.dataModel),
          HomeDecorCard,
          RecentOrders: fragment().shape(RecentOrders.dataModel),
          PrivateLabelCreditCard: fragment().shape(PrivateLabelCreditCard.dataModel),
          OnlineWorkshops: fragment().shape(OnlineWorkshops.dataModel),
          ...ShopByCategory.dataModel.component,
          UserList: fragment().shape(UserList.dataModel),
          SignIn: fragment().shape(SignIn.dataModel),
          PersonalizedHomeServices: fragment().shape(PersonalizedHomeServices.dataModel),
        }),
      }),
    }),
    seo: seoShape,
  }),
},
Spotlight,
VisualNavigation,
PromoVisualNavigation
);

export const guestB2cServerSideDataModel = extend({
  layouts: params({
    slug: string().isRequired(),
    customerType: string(),
  }).shape({
    id: string(),
    content: shape({
      __typename: string(),
      PersonalizedHomePage: fragment().shape({
        id: string(),
        pageTheme: string(),
        pageThemeSeparator: string(),
        sweepstakesBanner: shape(Sweepstakes.dataModel),
        internalMarketingBanner: internalMarketingBannerShape,
        bentoHero: shape(BentoHero.dataModel.component),
        quickLinks: shape({ AnchorLinks }),
        // you need this here too because the query is shaped at a lower level
        // it doesnt magically know how to merge the two heroCarousels because the nesting is odd
        heroCarousel: heroCarouselShape,
        componentOrder: shape({
          PersonalizedHomeServices: fragment().shape(PersonalizedHomeServices.dataModel),
          PopularCategories: fragment().shape(PopularCategories.dataModel),
          PrivateLabelCreditCard: fragment().shape(PrivateLabelCreditCard.dataModel),
          Recommendations: fragment().shape(Recommendations.dataModel),
          SpecialBuyOfTheDay: fragment().shape(SpecialBuyOfTheDay.dataModel),
          SpecialBuyOfTheWeek: fragment().shape(SpecialBuyOfTheWeek.dataModel),
          ...EventCountdownTimer.dataModel.component,
          Section: SectionDM,
          ...ShopByCategory.dataModel.component,
          SponsoredBanner: fragment().shape(SponsoredBanner.dataModel),
          OnlineWorkshops: fragment().shape(OnlineWorkshops.dataModel),
          RecentOrders: fragment().shape(RecentOrders.dataModel),
          SignIn: fragment().shape(SignIn.dataModel),
          ...Spotlight.dataModel.component,
        }),
      }),
    }),
    seo: seoShape,
  }),
},
Spotlight,
ShopByCategory,
VisualNavigation,
PromoVisualNavigation
);

export const guestB2cClientSideDataModel = extend({
  layouts: params({
    slug: string().isRequired(),
    customerType: string(),
  }).shape({
    id: string(),
  })
});

export const homepagePreviewByIDDataModelServerSide = extend({
  layout: params({
    layoutClass: customType('LayoutClass').enum(['PersonalizedHomePage']),
    id: string().isRequired(),
  }).shape({
    PersonalizedHomePage: fragment().shape({
      id: string(),
      pageTheme: string(),
      pageThemeSeparator: string(),
      quickLinks: shape({ AnchorLinks }),
      // you need this here too because the query is shaped at a lower level
      // it doesnt magically know how to merge the two heroCarousels because the nesting is odd
      heroCarousel: heroCarouselShape,
      componentOrder: shape({
        PersonalizedHomeServices: fragment().shape(PersonalizedHomeServices.dataModel),
        PopularCategories: fragment().shape(PopularCategories.dataModel),
        PrivateLabelCreditCard: fragment().shape(PrivateLabelCreditCard.dataModel),
        Recommendations: fragment().shape(Recommendations.dataModel),
        SpecialBuyOfTheDay: fragment().shape(SpecialBuyOfTheDay.dataModel),
        SpecialBuyOfTheWeek: fragment().shape(SpecialBuyOfTheWeek.dataModel),
        ...EventCountdownTimer.dataModel.component,
        Section: SectionDM,
        ...ShopByCategory.dataModel.component,
        SponsoredBanner: fragment().shape(SponsoredBanner.dataModel),
        OnlineWorkshops: fragment().shape(OnlineWorkshops.dataModel),
        RecentOrders: fragment().shape(RecentOrders.dataModel),
        SignIn: fragment().shape(SignIn.dataModel),
        ...Spotlight.dataModel.component,
      }),
      welcomeMessage: string(),
    }),
  }),
},
Spotlight
);

export const homepagePreviewByIDDataModelClientSide = extend({
  layout: params({
    layoutClass: customType('LayoutClass').enum(['PersonalizedHomePage']),
    id: string().isRequired(),
  }).shape({
    PersonalizedHomePage: fragment().shape({
      id: string(),
      pageTheme: string(),
      pageThemeSeparator: string(),
      quickLinks: shape({ AnchorLinks }),
      // you need this here too because the query is shaped at a lower level
      // it doesnt magically know how to merge the two heroCarousels because the nesting is odd
      componentOrder: shape({
        PopularCategories: fragment().shape(PopularCategories.dataModel),
        ...EventCountdownTimer.dataModel.component,
        ...ShopByCategory.dataModel.component,
        ...Spotlight.dataModel.component,
        ...SpotlightContainer.dataModel.specializedContainer,
        Recommendations: fragment().shape(Recommendations.dataModel),
        SpecialBuyOfTheDay: fragment().shape(SpecialBuyOfTheDay.dataModel),
        SpecialBuyOfTheWeek: fragment().shape(SpecialBuyOfTheWeek.dataModel),
        Section: SectionDM,
        PersonalizedHomeServices: fragment().shape(PersonalizedHomeServices.dataModel),
        SponsoredBanner: fragment().shape(SponsoredBanner.dataModel),
        PromotionCard: fragment().shape(PromotionCard.dataModel),
        RecentPurchases: fragment().shape(RecentPurchases.dataModel),
        RecentOrders: fragment().shape(RecentOrders.dataModel),
        LoyaltyBenefits: fragment().shape(LoyaltyBenefits.dataModel),
        OnlineWorkshops: fragment().shape(OnlineWorkshops.dataModel),
        UserList: fragment().shape(UserList.dataModel),
        SignIn: fragment().shape(SignIn.dataModel),
      }),
    }),
  }),
},
Spotlight,
SpotlightContainer,
ShopByCategory,
VisualNavigation,
PromoVisualNavigation
);

export const proDataModelServerSide = extend({
  layouts: params({
    slug: string().isRequired(),
    customerType: string(),
  }).shape({
    id: string(),
    content: shape({
      __typename: string(),
      PersonalizedHomePage: fragment().shape({
        id: string(),
        pageTheme: string(),
        pageThemeSeparator: string(),
        quickLinks: shape({ AnchorLinks }),
        sweepstakesBanner: shape(Sweepstakes.dataModel),
        internalMarketingBanner: internalMarketingBannerShape,
        // Commenting out bentoHero for pro as it's not currently used, may re-add later
        // bentoHero: shape(BentoHero.dataModel.component),
        // you need this here too because the query is shaped at a lower level
        // it doesnt magically know how to merge the two heroCarousels because the nesting is odd
        heroCarousel: heroCarouselShape,
      }),
    }),
    seo: seoShape,
  }),
},
PrivateLabelCreditCard,
Spotlight,
VisualNavigation,
PromoVisualNavigation
);

export const proDataModelClientSide = extend({
  layouts: params({
    slug: string().isRequired(),
    customerType: string(),
  }).shape({
    id: string(),
    content: shape({
      __typename: string(),
      PersonalizedHomePage: fragment().shape({
        id: string(),
        pageTheme: string(),
        pageThemeSeparator: string(),
        componentOrder: shape({
          Section: SectionDM,
          ...Spotlight.dataModel.component,
          ...EventCountdownTimer.dataModel.component,
          ...ShopByCategory.dataModel.component,
          Quotes: fragment().shape(Quotes.dataModel),
          Recommendations: fragment().shape(Recommendations.dataModel),
          SpecialBuyOfTheDay: fragment().shape(SpecialBuyOfTheDay.dataModel),
          SpecialBuyOfTheWeek: fragment().shape(SpecialBuyOfTheWeek.dataModel),
          SponsoredBanner: fragment().shape(SponsoredBanner.dataModel),
          PersonalizedHomeServices: fragment().shape(PersonalizedHomeServices.dataModel),
          Projects: fragment().shape(Projects.dataModel),
          PromotionCard: fragment().shape(PromotionCard.dataModel),
          RecentPurchases: fragment().shape(RecentPurchases.dataModel),
          LoyaltyBenefits: fragment().shape(LoyaltyBenefits.dataModel),
          UserList: fragment().shape(UserList.dataModel),
        }),
      }),
    }),
    seo: seoShape,
  }),
},
Spotlight,
VisualNavigation,
PromoVisualNavigation
);

export const eprocurementDataModelServerSide = extend({
  layouts: params({
    slug: string().isRequired(),
    customerType: string(),
  }).shape({
    id: string(),
    content: shape({
      __typename: string(),
      PersonalizedHomePage: fragment().shape({
        id: string(),
        internalMarketingBanner: internalMarketingBannerShape,
        bentoHero: shape(BentoHero.dataModel.component),
        // you need this here too because the query is shaped at a lower level
        // it doesnt magically know how to merge the two heroCarousels because the nesting is odd
        heroCarousel: heroCarouselShape,
      }),
    }),
    seo: seoShape,
  }),
},
PrivateLabelCreditCard,
Spotlight,
VisualNavigation,
PromoVisualNavigation
);

export const eprocurementDataModelClientSide = extend({
  layouts: params({
    slug: string().isRequired(),
    customerType: string(),
  }).shape({
    id: string(),
    content: shape({
      __typename: string(),
      PersonalizedHomePage: fragment().shape({
        id: string(),
        // you need this here too because the query is shaped at a lower level
        // it doesnt magically know how to merge the two heroCarousels because the nesting is odd
        componentOrder: shape({
          ...EventCountdownTimer.dataModel.component,
          ...Spotlight.dataModel.component,
          ...ShopByCategory.dataModel.component,
          Recommendations: fragment().shape(Recommendations.dataModel),
          Section: SectionDM,
          SpecialBuyOfTheDay: fragment().shape(SpecialBuyOfTheDay.dataModel),
          SpecialBuyOfTheWeek: fragment().shape(SpecialBuyOfTheWeek.dataModel),
          SponsoredBanner: fragment().shape(SponsoredBanner.dataModel),
          PersonalizedHomeServices: fragment().shape(PersonalizedHomeServices.dataModel),
          PromotionCard: fragment().shape(PromotionCard.dataModel),
          RecentPurchases: fragment().shape(RecentPurchases.dataModel),
          LoyaltyBenefits: fragment().shape(LoyaltyBenefits.dataModel),
          UserList: fragment().shape(UserList.dataModel),
        }),
      }),
    }),
    seo: seoShape,
  }),
},
Spotlight,
VisualNavigation,
PromoVisualNavigation
);
